import React, { useState } from 'react';
import { CheckCircle2Icon, AlertTriangleIcon, ShieldCheckIcon, SparklesIcon, AlertOctagonIcon, ThumbsUpIcon, XCircleIcon, MessageSquareIcon } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { PrimaryButton } from '../ui/PrimaryButton';
import { SecondaryButton } from '../ui/SecondaryButton';
interface PostJobReviewProps {
  ptpTask: string;
  ptpId: string;
  onSubmit: (report: PostJobReport) => void;
  onCancel: () => void;
}
export interface PostJobReport {
  taskCompleted: boolean;
  safetyIssues: boolean;
  nearMiss: boolean;
  notes: string;
  selectedIssues: string[];
}
const SAFETY_ISSUE_OPTIONS = ['Unexpected hazard encountered', 'PPE issue or non-compliance', 'Equipment malfunction', 'Weather-related stoppage', 'Communication breakdown', 'Unauthorized personnel in zone', 'Near miss — falling object', 'Near miss — struck by', 'Near miss — slip/trip/fall', 'Procedure deviation'];
// Mock AI analysis based on PTP data
const AI_ANALYSIS = {
  executionScore: 94,
  summary: 'Task executed within planned parameters. All critical controls were maintained throughout the operation.',
  findings: [{
    type: 'positive' as const,
    text: 'Task setup and preparation followed the plan as documented'
  }, {
    type: 'positive' as const,
    text: 'Environmental conditions were monitored throughout the task'
  }, {
    type: 'positive' as const,
    text: 'All crew members wore required PPE throughout the task'
  }, {
    type: 'warning' as const,
    text: 'Minor delay during equipment inspection — one item required replacement'
  }, {
    type: 'positive' as const,
    text: 'Work zone controls were maintained for all phases'
  }],
  recommendations: ['Schedule equipment inspection before next shift to avoid delays', 'Consider adding additional spotters for complex phases', 'Update plan to include backup communication protocol']
};
export function PostJobReviewScreen({
  ptpTask,
  ptpId,
  onSubmit,
  onCancel
}: PostJobReviewProps) {
  const [step, setStep] = useState<'form' | 'analysis'>('form');
  const [taskCompleted, setTaskCompleted] = useState(true);
  const [safetyIssues, setSafetyIssues] = useState(false);
  const [nearMiss, setNearMiss] = useState(false);
  const [selectedIssues, setSelectedIssues] = useState<string[]>([]);
  const [notes, setNotes] = useState('');
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const toggleIssue = (issue: string) => {
    setSelectedIssues(prev => prev.includes(issue) ? prev.filter(i => i !== issue) : [...prev, issue]);
  };
  const handleAnalyze = () => {
    setIsAnalyzing(true);
    setTimeout(() => {
      setIsAnalyzing(false);
      setStep('analysis');
    }, 2000);
  };
  const handleSubmit = () => {
    onSubmit({
      taskCompleted,
      safetyIssues,
      nearMiss,
      notes,
      selectedIssues
    });
  };
  if (isAnalyzing) {
    return <div className="flex flex-col h-full items-center justify-center" data-id="element-669">
        <motion.div animate={{
        rotate: 360
      }} transition={{
        repeat: Infinity,
        duration: 1.5,
        ease: 'linear'
      }} className="w-12 h-12 rounded-full border-3 border-gray-200 border-t-yellow-500 mb-4" style={{
        borderWidth: 3
      }} data-id="element-670" />
        <p className="text-gray-900 font-bold text-sm mb-1" data-id="element-671">
          Analyzing task execution…
        </p>
        <p className="text-gray-400 text-xs text-center" data-id="element-672">
          AI is reviewing PTP compliance and safety data
        </p>
      </div>;
  }
  if (step === 'analysis') {
    return <div className="flex flex-col h-full" data-id="element-673">
        <div className="mb-4" data-id="element-674">
          <div className="flex items-center gap-2 mb-2" data-id="element-675">
            <SparklesIcon className="w-5 h-5 text-yellow-500" data-id="element-676" />
            <h2 className="text-xl font-bold text-gray-900" data-id="element-677">
              Post-Job AI Analysis
            </h2>
          </div>
          <p className="text-gray-500 text-xs" data-id="element-678">{ptpId}</p>
        </div>

        <div className="flex-1 overflow-y-auto pb-6 -mx-2 px-2 space-y-4" data-id="element-679">
          {/* Execution Score */}
          <motion.div initial={{
          opacity: 0,
          y: 10
        }} animate={{
          opacity: 1,
          y: 0
        }} className="bg-white border border-gray-200 rounded-xl p-5 text-center" data-id="element-680">
            <p className="text-xs text-gray-400 uppercase tracking-wider font-semibold mb-2" data-id="element-681">
              Task Execution Score
            </p>
            <div className="relative inline-flex items-center justify-center" data-id="element-682">
              <svg className="w-24 h-24" viewBox="0 0 100 100" data-id="element-683">
                <circle cx="50" cy="50" r="42" fill="none" stroke="#e5e7eb" strokeWidth="8" data-id="element-684" />
                <circle cx="50" cy="50" r="42" fill="none" stroke={AI_ANALYSIS.executionScore >= 80 ? '#22c55e' : '#f59e0b'} strokeWidth="8" strokeLinecap="round" strokeDasharray={`${AI_ANALYSIS.executionScore / 100 * 264} 264`} transform="rotate(-90 50 50)" data-id="element-685" />
              </svg>
              <span className="absolute text-2xl font-black text-gray-900" data-id="element-686">
                {AI_ANALYSIS.executionScore}
              </span>
            </div>
            <p className="text-gray-500 text-xs mt-2" data-id="element-687">{AI_ANALYSIS.summary}</p>
          </motion.div>

          {/* Findings */}
          <motion.div initial={{
          opacity: 0,
          y: 10
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: 0.1
        }} data-id="element-688">
            <h3 className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-3" data-id="element-689">
              Key Findings
            </h3>
            <div className="space-y-2" data-id="element-690">
              {AI_ANALYSIS.findings.map((finding, i) => <motion.div key={i} initial={{
              opacity: 0,
              x: -10
            }} animate={{
              opacity: 1,
              x: 0
            }} transition={{
              delay: 0.15 + i * 0.06
            }} className={`flex items-start gap-2.5 p-3 rounded-lg border ${finding.type === 'positive' ? 'bg-green-50 border-green-200' : 'bg-amber-50 border-amber-200'}`} data-id="element-691">
                  {finding.type === 'positive' ? <CheckCircle2Icon className="w-4 h-4 text-green-600 flex-shrink-0 mt-0.5" data-id="element-692" /> : <AlertTriangleIcon className="w-4 h-4 text-amber-600 flex-shrink-0 mt-0.5" data-id="element-693" />}
                  <span className={`text-xs leading-relaxed ${finding.type === 'positive' ? 'text-green-700' : 'text-amber-700'}`} data-id="element-694">
                    {finding.text}
                  </span>
                </motion.div>)}
            </div>
          </motion.div>

          {/* User-reported issues */}
          {(safetyIssues || nearMiss) && <motion.div initial={{
          opacity: 0,
          y: 10
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: 0.3
        }} data-id="element-695">
              <h3 className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-3" data-id="element-696">
                Reported Issues
              </h3>
              <div className="bg-red-50 border border-red-200 rounded-xl p-4 space-y-2" data-id="element-697">
                {nearMiss && <div className="flex items-center gap-2" data-id="element-698">
                    <AlertOctagonIcon className="w-4 h-4 text-red-500" data-id="element-699" />
                    <span className="text-red-700 text-xs font-bold uppercase" data-id="element-700">
                      Near Miss Reported
                    </span>
                  </div>}
                {selectedIssues.map((issue, i) => <div key={i} className="flex items-start gap-2 ml-6" data-id="element-701">
                    <span className="text-red-500 text-xs" data-id="element-702">•</span>
                    <span className="text-red-700 text-xs" data-id="element-703">{issue}</span>
                  </div>)}
                {notes && <div className="mt-2 pt-2 border-t border-red-200" data-id="element-704">
                    <p className="text-red-600 text-xs italic" data-id="element-705">"{notes}"</p>
                  </div>}
              </div>
            </motion.div>}

          {/* Recommendations */}
          <motion.div initial={{
          opacity: 0,
          y: 10
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: 0.4
        }} data-id="element-706">
            <h3 className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-3" data-id="element-707">
              AI Recommendations
            </h3>
            <div className="bg-white border border-gray-200 rounded-xl p-4 space-y-2.5" data-id="element-708">
              {AI_ANALYSIS.recommendations.map((rec, i) => <div key={i} className="flex items-start gap-2.5" data-id="element-709">
                  <SparklesIcon className="w-3.5 h-3.5 text-yellow-500 flex-shrink-0 mt-0.5" data-id="element-710" />
                  <span className="text-gray-700 text-xs leading-relaxed" data-id="element-711">
                    {rec}
                  </span>
                </div>)}
            </div>
          </motion.div>
        </div>

        <div className="sticky bottom-0 z-10 pt-3 pb-2 space-y-3 bg-gray-50" data-id="element-712">
          <PrimaryButton onClick={handleSubmit} icon={<CheckCircle2Icon className="w-5 h-5" data-id="element-714" />} data-id="element-713">
            Confirm Task Completed
          </PrimaryButton>
        </div>
      </div>;
  }
  // Form step
  return <div className="flex flex-col h-full" data-id="element-715">
      <div className="mb-4" data-id="element-716">
        <h2 className="text-xl font-bold text-gray-900 mb-1" data-id="element-717">
          Post-Job Report
        </h2>
        <p className="text-gray-500 text-xs" data-id="element-718">{ptpTask}</p>
      </div>

      <div className="flex-1 overflow-y-auto pb-6 -mx-2 px-2 space-y-5" data-id="element-719">
        {/* Task Completion */}
        <div data-id="element-720">
          <h3 className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-3" data-id="element-721">
            Task Status
          </h3>
          <div className="grid grid-cols-2 gap-2" data-id="element-722">
            <button onClick={() => setTaskCompleted(true)} className={`p-3 rounded-xl border text-center transition-colors ${taskCompleted ? 'bg-green-50 border-green-300 ring-1 ring-green-100' : 'bg-white border-gray-200'}`} data-id="element-723">
              <ThumbsUpIcon className={`w-5 h-5 mx-auto mb-1.5 ${taskCompleted ? 'text-green-600' : 'text-gray-400'}`} data-id="element-724" />
              <p className={`text-xs font-bold ${taskCompleted ? 'text-green-700' : 'text-gray-400'}`} data-id="element-725">
                Completed
              </p>
            </button>
            <button onClick={() => setTaskCompleted(false)} className={`p-3 rounded-xl border text-center transition-colors ${!taskCompleted ? 'bg-amber-50 border-amber-300 ring-1 ring-amber-100' : 'bg-white border-gray-200'}`} data-id="element-726">
              <XCircleIcon className={`w-5 h-5 mx-auto mb-1.5 ${!taskCompleted ? 'text-amber-600' : 'text-gray-400'}`} data-id="element-727" />
              <p className={`text-xs font-bold ${!taskCompleted ? 'text-amber-700' : 'text-gray-400'}`} data-id="element-728">
                Not Completed
              </p>
            </button>
          </div>
        </div>

        {/* Safety Issues */}
        <div data-id="element-729">
          <h3 className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-3" data-id="element-730">
            Were there any safety issues?
          </h3>
          <div className="grid grid-cols-2 gap-2" data-id="element-731">
            <button onClick={() => setSafetyIssues(false)} className={`p-3 rounded-xl border text-center transition-colors ${!safetyIssues ? 'bg-green-50 border-green-300 ring-1 ring-green-100' : 'bg-white border-gray-200'}`} data-id="element-732">
              <ShieldCheckIcon className={`w-5 h-5 mx-auto mb-1.5 ${!safetyIssues ? 'text-green-600' : 'text-gray-400'}`} data-id="element-733" />
              <p className={`text-xs font-bold ${!safetyIssues ? 'text-green-700' : 'text-gray-400'}`} data-id="element-734">
                No Issues
              </p>
            </button>
            <button onClick={() => setSafetyIssues(true)} className={`p-3 rounded-xl border text-center transition-colors ${safetyIssues ? 'bg-red-50 border-red-300 ring-1 ring-red-100' : 'bg-white border-gray-200'}`} data-id="element-735">
              <AlertTriangleIcon className={`w-5 h-5 mx-auto mb-1.5 ${safetyIssues ? 'text-red-500' : 'text-gray-400'}`} data-id="element-736" />
              <p className={`text-xs font-bold ${safetyIssues ? 'text-red-600' : 'text-gray-400'}`} data-id="element-737">
                Yes, Issues
              </p>
            </button>
          </div>
        </div>

        {/* Near Miss */}
        <div data-id="element-738">
          <h3 className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-3" data-id="element-739">
            Any near miss incidents?
          </h3>
          <div className="grid grid-cols-2 gap-2" data-id="element-740">
            <button onClick={() => setNearMiss(false)} className={`p-3 rounded-xl border text-center transition-colors ${!nearMiss ? 'bg-green-50 border-green-300 ring-1 ring-green-100' : 'bg-white border-gray-200'}`} data-id="element-741">
              <p className={`text-xs font-bold ${!nearMiss ? 'text-green-700' : 'text-gray-400'}`} data-id="element-742">
                No Near Miss
              </p>
            </button>
            <button onClick={() => setNearMiss(true)} className={`p-3 rounded-xl border text-center transition-colors ${nearMiss ? 'bg-red-50 border-red-300 ring-1 ring-red-100' : 'bg-white border-gray-200'}`} data-id="element-743">
              <p className={`text-xs font-bold ${nearMiss ? 'text-red-600' : 'text-gray-400'}`} data-id="element-744">
                Near Miss
              </p>
            </button>
          </div>
        </div>

        {/* Issue Selection */}
        <AnimatePresence data-id="element-745">
          {(safetyIssues || nearMiss) && <motion.div initial={{
          opacity: 0,
          height: 0
        }} animate={{
          opacity: 1,
          height: 'auto'
        }} exit={{
          opacity: 0,
          height: 0
        }} transition={{
          duration: 0.2
        }} data-id="element-746">
              <h3 className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-3" data-id="element-747">
                Select applicable issues
              </h3>
              <div className="flex flex-wrap gap-2" data-id="element-748">
                {SAFETY_ISSUE_OPTIONS.map(issue => <button key={issue} onClick={() => toggleIssue(issue)} className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-colors ${selectedIssues.includes(issue) ? 'bg-red-100 border-red-300 text-red-600' : 'bg-white border-gray-200 text-gray-500'}`} data-id="element-749">
                    {issue}
                  </button>)}
              </div>
            </motion.div>}
        </AnimatePresence>

        {/* Notes */}
        <div data-id="element-750">
          <h3 className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-3 flex items-center gap-1.5" data-id="element-751">
            <MessageSquareIcon className="w-3.5 h-3.5" data-id="element-752" />
            Additional Notes
          </h3>
          <div className="bg-white border border-gray-200 rounded-xl p-3 focus-within:border-yellow-300 transition-colors" data-id="element-753">
            <textarea placeholder="Any observations, lessons learned, or notes for future tasks…" className="w-full bg-transparent text-gray-700 text-sm placeholder-gray-400 resize-none outline-none leading-relaxed h-20" value={notes} onChange={e => setNotes(e.target.value)} data-id="element-754" />
          </div>
        </div>
      </div>

      <div className="sticky bottom-0 z-10 pt-3 pb-2 space-y-3 bg-gray-50" data-id="element-755">
        <PrimaryButton onClick={handleAnalyze} icon={<SparklesIcon className="w-5 h-5" data-id="element-757" />} data-id="element-756">
          Analyze & Complete
        </PrimaryButton>
        <SecondaryButton onClick={onCancel} data-id="element-758">Cancel</SecondaryButton>
      </div>
    </div>;
}